import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { PRONTUARIO_TRAY_ACTIVE_COLOR } from '@/constants/sessionCallDailyTheme';
import { TUTORIAL_DEMONSTRACAO_BACKDROP_BG } from '@/constants/professionalTutorialDemo';
import type { TutorialDemonstracaoMediaState } from './useTutorialDemonstracaoMedia';
import { DailyCamIcon, DailyMicIcon } from './dailyTutorialMediaIcons';
import {
	TutorialDemonstracaoPrejoinCoachBalloon,
	type TutorialDemonstracaoPrejoinCoachStep,
} from './TutorialDemonstracaoPrejoinCoachBalloon';

type Props = {
	participantDisplayName: string;
	media: TutorialDemonstracaoMediaState;
	onJoin: () => void;
};

/** Daily Prebuilt `--body-bg` (card / conteúdo). */
const CARD_BG = '#121A24';
const CARD_BORDER = '#2B3F56';

const CARD_MAX_WIDTH_PX = 496;
const CARD_MAX_HEIGHT_PX = 650;

const DailyEffectsIcon = ({ className }: { className?: string }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		viewBox="0 0 25 24"
		className={cn('size-5 text-white', className)}
		aria-hidden
	>
		<circle cx="12" cy="12" r="4" fill="currentColor" />
		<path
			d="M12 3L12 6"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path
			d="M12 18L12 21"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path
			d="M3 12L6 12"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path
			d="M18 12L21 12"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path
			d="M6.34314 6.34314L7.75735 7.75735"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path
			d="M16.2427 16.2426L17.6569 17.6569"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path
			d="M17.6569 6.34314L16.2426 7.75735"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path
			d="M7.75732 16.2426L6.34311 17.6569"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const DailyNoiseCancellationIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		viewBox="0 0 16 16"
		className="size-4 text-white"
		aria-hidden
	>
		<path
			d="M5.33339 6.66667C5.33339 5.86333 5.47979 5.11252 5.87057 4.56977C6.24677 4.04727 6.88662 3.66667 8.00006 3.66667C9.1135 3.66667 9.75335 4.04727 10.1295 4.56977C10.5203 5.11252 10.6667 5.86333 10.6667 6.66667H10.6439C10.2883 6.66667 10.0001 6.95494 10.0001 7.31054V10.6895C10.0001 11.0451 10.2883 11.3333 10.6439 11.3333H10.9488C11.7197 11.3333 12.377 10.7746 12.5012 10.0138C12.6108 9.34239 12.6108 8.65761 12.5012 7.98618C12.3983 7.35588 11.9295 6.8643 11.3334 6.71419V6.66667C11.3334 5.80333 11.1798 4.88748 10.6706 4.18023C10.1468 3.45273 9.28662 3 8.00006 3C6.7135 3 5.85335 3.45273 5.32955 4.18023C4.82033 4.88748 4.66673 5.80333 4.66673 6.66667V6.71419C4.0706 6.8643 3.60182 7.35588 3.49892 7.98618C3.38929 8.65761 3.38929 9.34239 3.49892 10.0138C3.62313 10.7746 4.28043 11.3333 5.05133 11.3333H5.35618C5.71179 11.3333 6.00006 11.0451 6.00006 10.6895V7.31054C6.00006 6.95494 5.71179 6.66667 5.35618 6.66667H5.33339ZM3.66673 5.83333C3.66673 6.10948 3.44287 6.33333 3.16673 6.33333C2.89059 6.33333 2.66673 6.10948 2.66673 5.83333C2.66673 5.55719 2.89059 5.33333 3.16673 5.33333C3.44287 5.33333 3.66673 5.55719 3.66673 5.83333ZM3.16673 12.3333C3.44287 12.3333 3.66673 12.1095 3.66673 11.8333C3.66673 11.5572 3.44287 11.3333 3.16673 11.3333C2.89059 11.3333 2.66673 11.5572 2.66673 11.8333C2.66673 12.1095 2.89059 12.3333 3.16673 12.3333ZM2.66673 7.16667C2.66673 7.44281 2.44287 7.66667 2.16673 7.66667C1.89059 7.66667 1.66673 7.44281 1.66673 7.16667C1.66673 6.89052 1.89059 6.66667 2.16673 6.66667C2.44287 6.66667 2.66673 6.89052 2.66673 7.16667ZM2.16673 11C2.44287 11 2.66673 10.7761 2.66673 10.5C2.66673 10.2239 2.44287 10 2.16673 10C1.89059 10 1.66673 10.2239 1.66673 10.5C1.66673 10.7761 1.89059 11 2.16673 11ZM2.33339 8.83333C2.33339 9.10948 2.10954 9.33333 1.83339 9.33333C1.55725 9.33333 1.33339 9.10948 1.33339 8.83333C1.33339 8.55719 1.55725 8.33333 1.83339 8.33333C2.10954 8.33333 2.33339 8.55719 2.33339 8.83333ZM12.8334 6.33333C12.5573 6.33333 12.3334 6.10948 12.3334 5.83333C12.3334 5.55719 12.5573 5.33333 12.8334 5.33333C13.1095 5.33333 13.3334 5.55719 13.3334 5.83333C13.3334 6.10948 13.1095 6.33333 12.8334 6.33333ZM12.3334 11.8333C12.3334 12.1095 12.5573 12.3333 12.8334 12.3333C13.1095 12.3333 13.3334 12.1095 13.3334 11.8333C13.3334 11.5572 13.1095 11.3333 12.8334 11.3333C12.5573 11.3333 12.3334 11.5572 12.3334 11.8333ZM13.8334 7.66667C13.5573 7.66667 13.3334 7.44281 13.3334 7.16667C13.3334 6.89052 13.5573 6.66667 13.8334 6.66667C14.1095 6.66667 14.3334 6.89052 14.3334 7.16667C14.3334 7.44281 14.1095 7.66667 13.8334 7.66667ZM13.3334 10.5C13.3334 10.7761 13.5573 11 13.8334 11C14.1095 11 14.3334 10.7761 14.3334 10.5C14.3334 10.2239 14.1095 10 13.8334 10C13.5573 10 13.3334 10.2239 13.3334 10.5ZM14.1667 9.33333C13.8906 9.33333 13.6667 9.10948 13.6667 8.83333C13.6667 8.55719 13.8906 8.33333 14.1667 8.33333C14.4429 8.33333 14.6667 8.55719 14.6667 8.83333C14.6667 9.10948 14.4429 9.33333 14.1667 9.33333Z"
			fill="currentColor"
			fillRule="evenodd"
			clipRule="evenodd"
		/>
	</svg>
);

const DailySpeakerIcon = ({ className }: { className?: string }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		viewBox="0 0 24 24"
		className={cn('size-4 fill-current text-white', className)}
		aria-hidden
	>
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M3 10.5C3 9.67157 3.67157 9 4.5 9H5.96456L11.0501 4.83907C11.4987 4.47205 12.1186 4.3963 12.6423 4.6445C13.1661 4.89269 13.5 5.42042 13.5 6V18C13.5 18.5796 13.1661 19.1073 12.6423 19.3555C12.1186 19.6037 11.4987 19.528 11.0501 19.1609L5.96455 15H4.5C3.67157 15 3 14.3284 3 13.5V10.5ZM15.55 7.4C15.8814 7.15147 16.3515 7.21863 16.6 7.55L16 8C16.6 7.55 16.6003 7.5503 16.6005 7.55061L16.601 7.55131L16.6023 7.553L16.6056 7.55749L16.6154 7.5709C16.6233 7.58179 16.6339 7.59658 16.647 7.61514C16.673 7.65224 16.7087 7.70451 16.7519 7.77094C16.8382 7.90369 16.9547 8.09379 17.083 8.33333C17.3388 8.81074 17.6466 9.49402 17.8527 10.3181C18.2682 11.9802 18.2672 14.2271 16.6 16.45C16.3515 16.7814 15.8814 16.8485 15.55 16.6C15.2187 16.3515 15.1515 15.8814 15.4 15.55C16.7328 13.7729 16.7319 12.0198 16.3974 10.6819C16.2285 10.006 15.9738 9.43925 15.7608 9.04166C15.6548 8.84371 15.5603 8.69006 15.4943 8.58843C15.4613 8.53768 15.4355 8.5001 15.419 8.47666C15.4108 8.46494 15.405 8.45678 15.4017 8.45229L15.3997 8.44948L15.3993 8.44905L15.3989 8.44846L15.3988 8.44838C15.1517 8.1171 15.2192 7.64812 15.55 7.4ZM19.1049 5.55648C18.8599 5.22246 18.3906 5.15025 18.0565 5.3952C17.7228 5.6399 17.6504 6.10855 17.8945 6.44251L17.8946 6.44265L17.8946 6.44267L17.8947 6.44282L17.8949 6.44303L17.8951 6.44332L17.8952 6.44353L17.901 6.45161C17.9069 6.45995 17.9167 6.47384 17.9299 6.49313C17.9564 6.53171 17.9967 6.59176 18.0475 6.67183C18.1494 6.83209 18.2934 7.07177 18.4544 7.37926C18.7774 7.99584 19.1643 8.87586 19.4215 9.92809C19.9329 12.0203 19.9328 14.778 17.8952 17.5565C17.6503 17.8905 17.7225 18.3599 18.0565 18.6048C18.3906 18.8498 18.8599 18.7776 19.1049 18.4435C21.4673 15.222 21.4672 11.9797 20.8786 9.57191C20.5858 8.37415 20.1477 7.37917 19.7832 6.68325C19.6005 6.33449 19.4351 6.05854 19.3135 5.86724C19.2527 5.77153 19.2027 5.69682 19.1668 5.64458C19.1489 5.61845 19.1345 5.59793 19.1241 5.58319L19.1114 5.56543L19.1073 5.55985L19.1059 5.55789L19.1053 5.55712C19.1051 5.55679 19.1049 5.55648 18.5001 6L19.1049 5.55648Z"
		/>
	</svg>
);

const DailySelectChevron = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		viewBox="0 0 16 16"
		className="pointer-events-none absolute right-2 top-1/2 size-3 -translate-y-1/2 text-white/80"
		aria-hidden
	>
		<line
			x1="5"
			y1="7"
			x2="8"
			y2="10"
			stroke="currentColor"
			strokeLinecap="round"
			strokeWidth="2"
		/>
		<line
			x1="8"
			y1="10"
			x2="11"
			y2="7"
			stroke="currentColor"
			strokeLinecap="round"
			strokeWidth="2"
		/>
	</svg>
);

const HaircheckControlButton = ({
	onClick,
	ariaLabel,
	onLabel,
	offLabel,
	isOn,
	mutedIconBackground = false,
	children,
}: {
	onClick?: () => void;
	ariaLabel: string;
	onLabel: string;
	offLabel: string;
	isOn: boolean;
	mutedIconBackground?: boolean;
	children: ReactNode;
}) => (
	<button
		type="button"
		onClick={onClick}
		aria-label={ariaLabel}
		className="flex min-w-[3.5rem] flex-col items-center justify-start px-1 py-0.5 text-white transition-colors hover:bg-white/5"
	>
		<span
			className={cn(
				'flex size-6 items-center justify-center rounded-sm',
				mutedIconBackground && !isOn && 'bg-[#ea4335]',
			)}
		>
			{children}
		</span>
		<span className="mt-0.5 text-[10px] leading-none text-[#e8eaed]">
			{isOn ? onLabel : offLabel}
		</span>
	</button>
);

const DeviceSelect = ({
	id,
	name,
	value,
	onChange,
	options,
	placeholder,
}: {
	id: string;
	name: string;
	value: string;
	onChange: (value: string) => void;
	options: { deviceId: string; label: string }[];
	placeholder: string;
}) => (
	<div className="relative">
		<select
			id={id}
			name={name}
			value={value}
			onChange={(event) => onChange(event.target.value)}
			className={cn(
				'h-8 w-full appearance-none rounded border px-2.5 pr-7 text-xs text-[#e8eaed]',
				'focus:outline-none focus:ring-1 focus:ring-[#8ab4f8]/40',
			)}
			style={{ backgroundColor: CARD_BG, borderColor: CARD_BORDER }}
		>
			{options.length === 0 ? (
				<option value="">{placeholder}</option>
			) : (
				options.map((option) => (
					<option key={option.deviceId} value={option.deviceId}>
						{option.label}
					</option>
				))
			)}
		</select>
		<DailySelectChevron />
	</div>
);

const MicLevelDots = () => (
	<span className="ml-1 flex items-end gap-0.5" aria-hidden>
		<span
			className="w-[3px] rounded-full bg-[#34a853]"
			style={{ height: 3 }}
		/>
		<span
			className="w-[3px] rounded-full bg-[#34a853]"
			style={{ height: 3 }}
		/>
		<span
			className="w-[3px] rounded-full bg-[#34a853]"
			style={{ height: 3 }}
		/>
	</span>
);

const SettingsField = ({
	icon,
	label,
	htmlFor,
	children,
	trailing,
}: {
	icon: ReactNode;
	label: string;
	htmlFor: string;
	children: ReactNode;
	trailing?: ReactNode;
}) => (
	<div className="flex flex-col gap-1">
		<div className="flex items-center gap-1.5">
			{icon}
			<label
				htmlFor={htmlFor}
				className="text-xs leading-none text-[#e8eaed]"
			>
				{label}
			</label>
			{trailing}
		</div>
		{children}
	</div>
);

const TutorialDemonstracaoPrejoinScreen = ({
	participantDisplayName,
	media,
	onJoin,
}: Props) => {
	const videoRef = useRef<HTMLVideoElement>(null);
	const joinButtonRef = useRef<HTMLButtonElement>(null);
	const [coachStep, setCoachStep] =
		useState<TutorialDemonstracaoPrejoinCoachStep>('welcome');

	const {
		stream,
		camOn,
		micOn,
		cameras,
		microphones,
		speakers,
		selectedCameraId,
		selectedMicrophoneId,
		selectedSpeakerId,
		isLoading,
		error,
		toggleCamera,
		toggleMicrophone,
		selectCamera,
		selectMicrophone,
		selectSpeaker,
	} = media;

	useEffect(() => {
		const node = videoRef.current;
		if (!node) return;

		node.srcObject = camOn && stream ? stream : null;
	}, [camOn, stream]);

	return (
		<div
			className="flex h-full min-h-0 w-full items-center justify-center overflow-y-auto p-4"
			style={{ backgroundColor: TUTORIAL_DEMONSTRACAO_BACKDROP_BG }}
		>
			<TutorialDemonstracaoPrejoinCoachBalloon
				joinButtonRef={joinButtonRef}
				onStepChange={setCoachStep}
			/>

			<div
				className="flex w-full flex-col overflow-hidden rounded-lg border shadow-lg"
				style={{
					backgroundColor: CARD_BG,
					borderColor: CARD_BORDER,
					maxWidth: CARD_MAX_WIDTH_PX,
					maxHeight: CARD_MAX_HEIGHT_PX,
				}}
			>
				{/* card-content — padding 24px 0 */}
				<div className="shrink-0 py-4">
					<div className="flex items-center justify-between gap-2 px-5">
						<h2 className="text-[15px] font-normal leading-tight text-[#e8eaed]">
							Você está pronto para participar?
						</h2>
						<button
							type="button"
							id="haircheck-join"
							ref={joinButtonRef}
							onClick={onJoin}
							disabled={isLoading || coachStep === 'welcome'}
							className={cn(
								'shrink-0 rounded-lg px-3.5 py-1.5 text-xs font-medium',
								'text-[#0f1722] transition-opacity hover:opacity-90 disabled:opacity-50',
								coachStep === 'join' &&
									'ring-1 ring-[rgb(27,235,185)]/35 ring-offset-1 ring-offset-[#121A24]',
							)}
							style={{
								backgroundColor: PRONTUARIO_TRAY_ACTIVE_COLOR,
							}}
						>
							<span>Entrar</span>
						</button>
					</div>
				</div>

				{/* card-content noPadding — videoContainer */}
				<div className="relative w-full shrink-0">
					<div
						className="relative aspect-video w-full overflow-hidden"
						style={{ backgroundColor: CARD_BG }}
					>
						<div className="absolute inset-0">
							{camOn && stream ? (
								<video
									ref={videoRef}
									autoPlay
									muted
									playsInline
									data-local="true"
									data-mirrored="true"
									className="size-full object-contain transition-opacity duration-[250ms]"
									style={{ transform: 'scale(-1, 1)' }}
								/>
							) : (
								<div className="flex size-full items-center justify-center bg-[#2B3F56]">
									<span className="text-sm text-white/80">
										{participantDisplayName}
									</span>
								</div>
							)}

							{/* tile-info — colado no canto inferior esquerdo */}
							<div className="absolute bottom-0 left-0 z-10 max-w-full">
								<div className="flex max-w-full items-center gap-1 bg-black/60 px-1.5 py-1">
									<span
										className={cn(
											'flex shrink-0 items-center justify-center',
											(microphones.length === 0 ||
												!micOn) &&
												'rounded-sm bg-[#ea4335] p-0.5',
										)}
									>
										<DailyMicIcon
											muted={
												microphones.length === 0 ||
												!micOn
											}
											className="size-3.5"
										/>
									</span>
									<span className="truncate text-[11px] leading-none text-white">
										{participantDisplayName}
									</span>
								</div>
							</div>
						</div>
					</div>
				</div>

				{error ? (
					<p className="px-6 pt-3 text-sm text-red-300">{error}</p>
				) : null}

				{/* toolbar — padding 8px */}
				<div className="flex shrink-0 items-center justify-center px-1 py-1.5">
					<div className="flex items-center justify-start">
						<HaircheckControlButton
							onClick={toggleCamera}
							ariaLabel={
								!cameras.length
									? 'Sem câmera'
									: camOn
									  ? 'Desligar a câmera'
									  : 'Ativar a câmera'
							}
							onLabel="Desligar"
							offLabel={
								cameras.length > 0 ? 'Ativar' : 'Sem câmera'
							}
							isOn={cameras.length > 0 && camOn}
							mutedIconBackground
						>
							<DailyCamIcon
								muted={cameras.length === 0 || !camOn}
								className="size-5"
							/>
						</HaircheckControlButton>

						<HaircheckControlButton
							onClick={toggleMicrophone}
							ariaLabel={
								!microphones.length
									? 'Sem microfone'
									: micOn
									  ? 'Desativar microfone'
									  : 'Ativar microfone'
							}
							onLabel="Silenciar"
							offLabel={
								microphones.length > 0
									? 'Ativar'
									: 'Sem microfone'
							}
							isOn={microphones.length > 0 && micOn}
							mutedIconBackground
						>
							<DailyMicIcon
								muted={microphones.length === 0 || !micOn}
								className="size-5"
							/>
						</HaircheckControlButton>

						<HaircheckControlButton
							ariaLabel="Efeitos"
							onLabel="Efeitos"
							offLabel="Efeitos"
							isOn
						>
							<DailyEffectsIcon />
						</HaircheckControlButton>

						<HaircheckControlButton
							ariaLabel="Reduzir"
							onLabel="Reduzido"
							offLabel="Reduzir"
							isOn={false}
						>
							<DailyNoiseCancellationIcon />
						</HaircheckControlButton>
					</div>
				</div>

				<div
					className="shrink-0 border-t"
					style={{ borderColor: CARD_BORDER }}
					aria-hidden
				/>

				{/* audio-video-settings */}
				<div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
					<div className="flex flex-col gap-3.5">
						<SettingsField
							icon={
								<DailyCamIcon
									muted={cameras.length === 0 || !camOn}
									className="size-4"
								/>
							}
							label="Câmera"
							htmlFor="prejoin-camera"
						>
							<DeviceSelect
								id="prejoin-camera"
								name="settings-camera"
								value={selectedCameraId}
								onChange={selectCamera}
								options={cameras}
								placeholder="Nenhuma câmera encontrada"
							/>
						</SettingsField>

						<SettingsField
							icon={
								<DailyMicIcon
									muted={microphones.length === 0 || !micOn}
									className="size-4"
								/>
							}
							label="Microfone"
							htmlFor="prejoin-microphone"
							trailing={micOn ? <MicLevelDots /> : null}
						>
							<DeviceSelect
								id="prejoin-microphone"
								name="settings-microphone"
								value={selectedMicrophoneId}
								onChange={selectMicrophone}
								options={microphones}
								placeholder="Nenhum microfone encontrado"
							/>
						</SettingsField>

						<SettingsField
							icon={<DailySpeakerIcon className="size-4" />}
							label="Alto-falantes"
							htmlFor="prejoin-speakers"
						>
							<DeviceSelect
								id="prejoin-speakers"
								name="settings-speakers"
								value={selectedSpeakerId}
								onChange={selectSpeaker}
								options={speakers}
								placeholder="Nenhum alto-falante encontrado"
							/>
						</SettingsField>
					</div>
				</div>
			</div>
		</div>
	);
};

export { TutorialDemonstracaoPrejoinScreen };
